/* ═══════════════════════════════════════════════════════════════════
   waveMetrics.js — THE single source of truth for every wave metric.

   The whole DSP reads metric labels, formulas, bands, and colors from
   THIS FILE. Any change here propagates everywhere automatically:
   - LiveWaveDashboard hero gauge
   - WaveOrchestrationViz live mode
   - Campaigns / Insights / Investor / Demo Studio pages
   - SupportPage glossary
   - Brand Rep Guide & PRD docs (kept in sync manually)

   ═══════════════════════════════════════════════════════════════════
   THE PRIMARY METRIC: Wave Pulse (0-100)
   "How close your wave looks to a real organic trend, right now."
   ═══════════════════════════════════════════════════════════════════
   Composite of:
     • 40% — post density (are creators firing on time?)
     • 30% — cross-platform spread (TikTok ↔ IG ↔ YT engagement crossover)
     • 20% — platform balance (no single platform dominates)
     • 10% — reply compression (people replying to each other, not just the brand)
   ═══════════════════════════════════════════════════════════════════ */

// ─────────────────────────────────────────────────────────────────────
// Wave Pulse bands — the at-a-glance "is this good?" answer
// ─────────────────────────────────────────────────────────────────────
export const WAVE_PULSE_BANDS = [
  { min: 85, label: 'Forming a trend',   color: '#4ade80', glow: 'rgba(74,222,128,0.45)',  hint: 'This looks like organic virality. Hold course.' },
  { min: 70, label: 'Compounding',       color: '#00D4FF', glow: 'rgba(0,212,255,0.45)',   hint: 'Trajectory is good — engagement is doubling on itself.' },
  { min: 50, label: 'Building',          color: '#FFB020', glow: 'rgba(255,176,32,0.45)',  hint: 'Wave is working but needs a push to compound.' },
  { min: 30, label: 'Needs boost',       color: '#FF8800', glow: 'rgba(255,136,0,0.45)',   hint: 'Reacceleration recommended — fire a reply storm or add patient zero.' },
  { min: 0,  label: 'Stalled',           color: '#FF4D6B', glow: 'rgba(255,77,107,0.45)',  hint: 'Intervention required — pause, diagnose, then customize.' },
];

export function getPulseBand(score) {
  const s = Math.max(0, Math.min(100, Number(score) || 0));
  return WAVE_PULSE_BANDS.find(b => s >= b.min) || WAVE_PULSE_BANDS[WAVE_PULSE_BANDS.length - 1];
}

// ─────────────────────────────────────────────────────────────────────
// Wave Pulse derivation — works on either backend payloads
// (`/live-state` totals) OR static demo data (CampaignDetailPage mocks).
// Falls back to a believable score so the UI never shows "undefined".
// ─────────────────────────────────────────────────────────────────────
export function derivePulseFromState(state) {
  if (!state) return 0;
  // Preferred: backend now ships `totals.wave_pulse` (server-computed).
  if (typeof state?.totals?.wave_pulse === 'number') return Math.round(state.totals.wave_pulse);
  if (typeof state?.wave_pulse === 'number') return Math.round(state.wave_pulse);

  // Fallback derivation from live totals (used during the transition window).
  const totals = state.totals || {};
  const firedRatio = totals.total_posts > 0 ? totals.fired_posts / totals.total_posts : 0;
  const engPerFired = totals.fired_posts > 0 ? totals.total_engagement / totals.fired_posts : 0;
  // Heuristic: 40% post density + 60% engagement intensity (capped at ~5K eng per fired post = max signal)
  const density = firedRatio * 100;
  const intensity = Math.min(100, (engPerFired / 5000) * 100);
  return Math.round(density * 0.4 + intensity * 0.6);
}

// ─────────────────────────────────────────────────────────────────────
// Metric definitions — read everywhere via MetricWithExplainer
// ─────────────────────────────────────────────────────────────────────
export const METRICS = {
  wave_pulse: {
    label: 'Wave Pulse',
    short: '0–100 score',
    definition: 'How close your wave looks to a real organic trend, right now. Maps directly to what TikTok, Instagram Reels, and YouTube Shorts algorithms reward.',
    formula: '40% post density (auto-scheduler guarantees) + 30% cross-engagement (creator-to-creator replies) + 20% multi-platform spread + 10% comment depth',
    impact: 'One number tells you if the wave is forming a trend, building, or stalled. 85+ = trend forming, 70–84 = compounding, 50–69 = building, 30–49 = needs boost, below 30 = stalled.',
  },
  brand_pulse: {
    label: 'Brand Pulse',
    short: 'Lifetime score',
    definition: 'Your brand\'s average Wave Pulse across every wave you\'ve ever run.',
    formula: 'mean(Wave Pulse) of all completed waves, percentile-ranked vs other brands in your vertical',
    impact: 'Above 80 = top decile. This is your track record at making waves that trend, not a single live wave.',
  },
  reach: {
    label: 'Reach',
    short: 'Unique humans',
    definition: 'The number of unique humans who saw at least one post from this wave.',
    formula: 'de-duplicated unique viewers across TikTok + Instagram + YouTube',
    impact: 'Different from Views (which counts every play, including the same person twice). Reach is the true audience size.',
  },
  views: {
    label: 'Views',
    short: 'Total plays',
    definition: 'Cumulative video plays across all wave posts.',
    formula: 'sum of platform-reported view counts (can include repeat viewers)',
    impact: 'How much screen time your wave received. Views ≥ Reach because one person can watch multiple posts.',
  },
  impressions: {
    label: 'Impressions',
    short: 'Surface shows',
    definition: 'Number of times your content appeared on a screen, including scroll-bys.',
    formula: 'sum of platform-reported impressions (includes muted autoplay and quick scrolls)',
    impact: 'The largest number — represents algorithmic distribution. Use to compare against paid social CPM.',
  },
  engagement_rate: {
    label: 'Engagement Rate',
    short: '% interaction',
    definition: 'Percentage of people who saw your post who also interacted with it.',
    formula: '(likes + comments + shares) ÷ reach × 100',
    impact: 'Industry baseline is 1–3%. Above 5% means the creator struck a nerve. Wave averages 6.4% because synchronized creators get higher For You / Explore placement.',
  },
  engagements: {
    label: 'Engagements',
    short: 'Total actions',
    definition: 'Absolute count of likes + comments + shares.',
    formula: 'likes + comments + shares',
    impact: 'The raw signal of audience action. Compare across waves to see relative impact.',
  },
  engagement_velocity: {
    label: 'Engagement Velocity',
    short: 'Hourly slope',
    definition: 'How fast engagement is accelerating, hour by hour, since the wave launched.',
    formula: 'engagements per hour over time (rising slope = compounding, flat = saturated, falling = decaying)',
    impact: 'The shape of the curve tells you if Wave Pulse is heading up or down before the score changes. Rising velocity in hours 2–8 = algorithm pickup.',
  },
  roas: {
    label: 'ROAS',
    short: 'Return per $',
    definition: 'Return on Ad Spend — revenue generated per dollar invested in the wave.',
    formula: 'revenue attributed to wave ÷ wave spend',
    impact: 'Industry benchmark for creator marketing is 3–5×. Wave brands typically see 6–9× because synchronized bursts trigger algorithmic boosts that paid social pays for.',
  },
  ctr: {
    label: 'CTR',
    short: 'Click-through',
    definition: 'Percentage of viewers who clicked through to your store or product page.',
    formula: 'clicks ÷ impressions × 100',
    impact: 'Paid social CTR is typically 1–2%. Creator-driven CTR on waves often lands 3–6% because viewers trust the recommendation.',
  },
  vcr: {
    label: 'Completion Rate',
    short: 'Watched fully',
    definition: 'Percentage of viewers who watched the entire video.',
    formula: 'completed views ÷ total views × 100',
    impact: 'Category benchmark is 55–65%. Above 75% signals hook + pacing are working — algorithms reward completion heavily.',
  },
  sentiment: {
    label: 'Sentiment',
    short: 'Audience tone',
    definition: 'AI-classified emotional tone of comments and replies.',
    formula: 'NLP sentiment classifier on all comment text, weighted by engagement volume',
    impact: 'Very Positive / Positive / Neutral / Negative. Above Neutral = trust + recommendation behavior follows. Negative = brand safety review needed.',
  },
  burst_power: {
    label: 'Burst Power',
    short: 'Creator score',
    definition: 'A creator\'s first-hour engagement rate versus their peer-tier median.',
    formula: '(their first-hour eng rate ÷ peer median eng rate) × 50, clamped 0-100',
    impact: 'Per-creator score. Above 80 = patient-zero candidate. Below 50 = pair with stronger creator or move to amplifier role.',
  },
  trend_fit: {
    label: 'Trend Fit',
    short: 'DNA match',
    definition: 'How well a creator\'s audience overlaps with the audiences other creators in this wave have.',
    formula: 'audience-embedding cosine similarity against the wave cohort centroid',
    impact: 'High Trend Fit = cross-platform spread will be strong. Low Trend Fit = the creator may bring reach but won\'t amplify others.',
  },
};

// Legacy aliases — kept so we don't have to chase every site at once.
// All NEW code should reference METRICS directly.
METRICS.pulse = METRICS.wave_pulse;
METRICS.wave_impact = METRICS.brand_pulse;
METRICS.velocity = METRICS.engagement_velocity;
METRICS.resonance = METRICS.trend_fit;
